import { ApprovalableTypeEnum } from 'api-hooks/common';
import { ApprovalItemWorkflowEnum } from 'api-hooks/sale/common';
import {
  HotelSaleModel,
  getHotelSalesKey,
  useCreateHotelSale,
} from 'api-hooks/sale/hotel';
import { KIND, Notification } from 'baseui/notification';
import { NavigationRoutes } from 'common/constants';
import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import { isDataDraft } from 'hooks/use-approval-action';
import useDialog from 'hooks/use-dialog';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import RequestApprovalModal from 'legacy-modules/approval/approval/components/request-modal';
import { SeparatorVertical } from 'legacy-modules/styles';
import * as React from 'react';
import { NavigationProps } from 'types';

import HotelSaleForm from './components/form';
import { HotelSaleFormType } from './components/form-type';

export type HotelSaleCreateParams = {
  defaultValues?: HotelSaleFormType;
  hotelSale?: HotelSaleModel;
};

export default function HotelSaleCreate(
  props: NavigationProps<NavigationRoutes.HotelSalesCreate>,
) {
  const { navigate } = useNavigation();

  const { mutateAsync, error } = useCreateHotelSale();
  const dialog = useDialog();
  const defaultValues = props.navigation.params?.defaultValues;
  const permission = useGetCrudPermission('hotel-sales');
  const hotelPurchasePermission = useGetCrudPermission('hotel-purchases');

  return (
    <>
      <HotelSaleForm
        defaultValues={defaultValues}
        {...props}
        renderError={
          !!error &&
          !!error.message && (
            <>
              <Notification
                overrides={{
                  Body: { style: { width: 'auto', wordBreak: 'break-all' } },
                }}
                kind={KIND.negative}
                closeable
              >
                {() => error.message}
              </Notification>
              <SeparatorVertical $margin={8} />
            </>
          )
        }
        onSubmit={async (input, form) => {
          const result = await mutateAsync(input);

          const onDismiss = () => {
            queryClient.refetchQueries([getHotelSalesKey()[0]]);

            if (hotelPurchasePermission.create) {
              navigate(NavigationRoutes.HotelPurchasesCreate, {
                hotelSale: result.data,
              });
            }

            notification.success({ message: result?.message });
          };

          const onDismissCancel = () => {
            queryClient.refetchQueries([getHotelSalesKey()[0]]);

            if (permission.view) {
              navigate(NavigationRoutes.HotelSalesView, {
                id: result.data.id,
              });
            }
          };

          const isDraft = isDataDraft(result.data.status);

          if (isDraft) {
            dialog.showCustom({
              render: (onClose) => (
                <RequestApprovalModal
                  onClose={onClose}
                  data={{
                    approvalableId: result.data.id,
                    approvalableType: ApprovalableTypeEnum.Sale,
                    workflow: ApprovalItemWorkflowEnum.sale,
                  }}
                  onDismissSuccess={onDismissCancel}
                  onDismissCancel={onDismissCancel}
                />
              ),
            });
          } else {
            onDismiss();
          }

          return result.data;
        }}
      />
    </>
  );
}
